"use client";

/**
 * AgentHero — client island that owns the hero section wrapper, cubes layer,
 * and mouse position state for the cube wave animation.
 *
 * Architecture (§9.3 Fix 3 / §1.2):
 * - This component is "use client" so it can own useState + event handlers.
 * - Page content (breadcrumbs, kicker, H1, lead, buttons, identity card)
 *   is passed as children so it stays server-rendered (SEO crawlable).
 * - BackgroundCubes wiring: heroMousePos prop → BackgroundCubes syncs it
 *   into heroMouseRef via useEffect (see BackgroundCubes.tsx line 161-163).
 *   Passing the prop directly is correct — do not dual-wire via the module ref.
 *
 * Uses .pseo-hero-bg (mask-image fade, §9.3 Fix 2) and .pseo-hero-content
 * (96vh min-height, §9.3 Fix 1) — NOT the landing's .hero-bg / .hero-content.
 */

import { useState, useCallback, Suspense } from "react";
import dynamic from "next/dynamic";

const BackgroundCubes = dynamic(
  () => import("@/components/landing/BackgroundCubes"),
  { ssr: false }
);

export default function AgentHero({ children }: { children: React.ReactNode }) {
  const [heroMousePos, setHeroMousePos] = useState<{ x: number; y: number } | null>(null);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHeroMousePos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  }, []);

  const onMouseLeave = useCallback(() => setHeroMousePos(null), []);

  return (
    <section
      className="hero-section"
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      {/* Cube layer — fades out via mask-image over bottom 25% (.pseo-hero-bg) */}
      <div className="pseo-hero-bg">
        <Suspense fallback={null}>
          <BackgroundCubes heroMousePos={heroMousePos} />
        </Suspense>
      </div>

      {/* Content — server-rendered children stay SEO crawlable */}
      <div className="pseo-hero-content">{children}</div>
    </section>
  );
}
